// ABOUTME: Drops agentState out of 'thinking' when the backend goes completely silent on a turn.
// ABOUTME: Silence is often deliberate agent behaviour, so this rests the UI rather than erroring.

import { useCallback, useEffect, useRef } from 'react';

/**
 * How long the backend may say NOTHING AT ALL before we stop claiming the agent is thinking.
 *
 * This is not "how long an answer may take" — see the `activity` param, which restarts the clock
 * on every scrap of agent transcript. It is the gap after which we conclude nothing is coming.
 * Tune here; it is the only knob.
 */
export const THINKING_SILENCE_MS = 4000;

/**
 * `agentState` goes to 'thinking' when the guest stops talking, and the ONLY things that move it
 * off again are the agent's first audio chunk, a `stop_audio` control, or the guest speaking again.
 * If the agent simply never answers, nothing fires and the UI sits in 'thinking' forever — until
 * the guest talks again purely to shake it loose.
 *
 * That state is reachable in ordinary, non-broken use, which is the whole reason this resets to
 * rest instead of surfacing an error (JC, 2026-09-01):
 *
 *   - The guest tells the agent not to reply. It obeys. They keep talking; it keeps not replying.
 *     This is CORRECT behaviour, and it must not read as a hung interface.
 *   - The guest trails off mid-sentence, or says something the agent has nothing to answer.
 *   - The turn genuinely fails.
 *
 * The client cannot tell these apart — the WS protocol has no turn-complete or declined-to-answer
 * message (only `transcript`, binary `audio`, and `control/stop_audio`). So the rule is deliberately
 * about EVIDENCE, not about elapsed time since the guest finished: any agent transcript is proof a
 * response is being composed and restarts the clock, so a slow tool call or a long generation never
 * trips this. Only true silence does.
 *
 * Resting rather than erroring also means the "don't reply" conversation works exactly as the guest
 * expects: they talk (cluster shows speaking), they stop, it settles to idle, they talk again. No
 * spinner, no stuck pose, nothing to dismiss.
 *
 * @param {object}   p
 * @param {string}   p.agentState  current transport state; the watchdog only arms on 'thinking'
 * @param {Function} p.onSilence   called when the window elapses with no agent activity
 * @param {*}        [p.activity]  any value that CHANGES when agent output arrives. Every change
 *                                 restarts the window. Prop-driven callers only — App.js uses the
 *                                 returned poke instead, precisely so ticking does NOT re-render.
 * @param {number}   [p.timeoutMs]
 * @returns {Function} notifyActivity — restarts the window while armed, no-op otherwise.
 *   Render-free (the timer lives behind a ref, no state) and IDENTITY-STABLE for the life of the
 *   component, so it is safe to capture in long-lived closures like ws.onmessage. This exists
 *   because the original wiring bumped a useState counter on every typed WS message: during agent
 *   speech that re-rendered the whole App tree several times a second, and on phones the
 *   main-thread churn delayed the reel's rVFC boundary callbacks — the welcome-idle wrap fired
 *   late and 360-section frames bled through as a visible jump (mobile jank pass, 2026-09-01).
 */
export function useThinkingWatchdog({
  agentState,
  onSilence,
  activity,
  timeoutMs = THINKING_SILENCE_MS,
}) {
  const timerRef = useRef(null);
  const armedRef = useRef(false);
  // Live copies for the poke, so a tick never re-arms with a stale callback or window length.
  // Same latest-ref idiom as useVideoPlayer's onVideoFrameRef/transitionToRef.
  const paramsRef = useRef({ onSilence, timeoutMs });
  paramsRef.current = { onSilence, timeoutMs };

  const notifyActivity = useCallback(() => {
    if (!armedRef.current) return; // not thinking (or already torn down) — nothing to keep alive
    const { onSilence: cb, timeoutMs: ms } = paramsRef.current;
    if (typeof cb !== 'function') return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(cb, ms);
  }, []);

  useEffect(() => {
    if (agentState !== 'thinking') return undefined;
    if (typeof onSilence !== 'function') return undefined;

    armedRef.current = true;
    timerRef.current = setTimeout(onSilence, timeoutMs);
    // Cleanup runs whenever `activity` changes too, which is what re-arms the window on each new
    // scrap of transcript rather than letting the original deadline stand — and it disarms the
    // poke, so a tick racing the teardown is a no-op rather than a resurrection.
    return () => {
      armedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [agentState, onSilence, activity, timeoutMs]);

  return notifyActivity;
}

export default useThinkingWatchdog;
